/** Same-origin calls to the portal Worker. Writes carry the custom header the Worker requires. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const RECOVERY = " Refresh the page or try again in a minute.";

async function parse<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => null)) as {
    data?: T;
    error?: { code?: string; message?: string };
  } | null;
  if (!res.ok) {
    throw new ApiError(
      res.status,
      body?.error?.code ?? "ERROR",
      body?.error?.message ?? `Something went wrong.${RECOVERY}`,
    );
  }
  return (body?.data ?? (body as unknown)) as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, { headers: { Accept: "application/json" } });
  } catch {
    throw new ApiError(
      0,
      "NETWORK",
      `We could not reach the portal.${RECOVERY}`,
    );
  }
  return parse<T>(res);
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Partner-Write": "1",
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(
      0,
      "NETWORK",
      `We could not reach the portal.${RECOVERY}`,
    );
  }
  return parse<T>(res);
}

const WRONG_CODE =
  "That code is wrong or expired. Request a new code and try again.";

/**
 * A failed `/api/auth/*` call as a sentence that says how to recover. The
 * Worker's proxy answers its own `{ error: { code, message } }`; Better Auth
 * answers a top-level, bare `message` ("Invalid OTP", and "Too many attempts"
 * as a 403, not a 429). No code exists yet on the send step, so only the
 * sign-in step may blame one.
 */
function authFailureMessage(
  path: string,
  status: number,
  body: { message?: string; error?: { message?: string } } | null,
): string {
  if (body?.error?.message) return body.error.message;
  if (status >= 500) return "Sign-in is busy. Try again in a minute.";
  if (status === 429) {
    return "Too many tries. Wait a minute and request a new code.";
  }
  const text = body?.message?.trim().replace(/\.+$/, "");
  if (path.startsWith("email-otp/send-")) {
    return `${text ?? "We could not send a code"}. Check the address and try again.`;
  }
  if (!text || /^(invalid otp|otp expired)$/i.test(text)) return WRONG_CODE;
  if (/too many attempts/i.test(text)) {
    return "Too many tries on this code. Request a new code and try again.";
  }
  return `${text}. Request a new code and try again.`;
}

/** Better Auth routes, re-served on this origin. */
export async function authPost(path: string, body: unknown): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`/api/auth/${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(
      0,
      "NETWORK",
      `We could not reach the sign-in service.${RECOVERY}`,
    );
  }
  if (!res.ok) {
    const b = (await res.json().catch(() => null)) as {
      message?: string;
      error?: { code?: string; message?: string };
    } | null;
    throw new ApiError(
      res.status,
      b?.error?.code ?? "AUTH",
      authFailureMessage(path, res.status, b),
    );
  }
}

export function messageOf(error: unknown): string {
  return error instanceof Error
    ? error.message
    : `Something went wrong.${RECOVERY}`;
}
